import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export const CartContext = createContext(null);

const readStoredValue = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const savedValue = window.localStorage.getItem(key);
    return savedValue ? JSON.parse(savedValue) : fallback;
  } catch {
    return fallback;
  }
};

const normalizeQuantity = (quantity) => {
  const parsedQuantity = Number(quantity);
  return Number.isFinite(parsedQuantity)
    ? Math.max(1, Math.floor(parsedQuantity))
    : 1;
};

const normalizeCartItems = (value) => {
  if (!Array.isArray(value)) return [];

  const normalizedItems = value
    .filter(
      (item) => item && Number.isInteger(Number(item.id)) && Number(item.id) > 0
    )
    .map((item) => ({
      id: Number(item.id),
      size: typeof item.size === "string" ? item.size : "",
      color: typeof item.color === "string" ? item.color : "",
      quantity: normalizeQuantity(item.quantity),
    }));

  return normalizedItems.reduce((items, item) => {
    const existingIndex = items.findIndex(
      (candidate) =>
        candidate.id === item.id &&
        candidate.size === item.size &&
        candidate.color === item.color
    );

    if (existingIndex === -1) return [...items, item];
    return items.map((candidate, index) =>
      index === existingIndex
        ? { ...candidate, quantity: candidate.quantity + item.quantity }
        : candidate
    );
  }, []);
};

const normalizeWishlistItems = (value) => {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value.map(Number).filter((id) => Number.isInteger(id) && id > 0)
    ),
  ];
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [storageReady, setStorageReady] = useState(false);

  useEffect(() => {
    const legacyCart = readStoredValue("cartItems", []);
    const storedCart = readStoredValue("tellason-cart-v1", legacyCart);
    setCartItems(normalizeCartItems(storedCart));
    setWishlistItems(
      normalizeWishlistItems(readStoredValue("tellason-wishlist-v1", []))
    );
    setStorageReady(true);
  }, []);

  useEffect(() => {
    if (!storageReady) return;
    try {
      window.localStorage.setItem(
        "tellason-cart-v1",
        JSON.stringify(cartItems)
      );
    } catch {
      // Shopping remains fully usable when storage is unavailable.
    }
  }, [cartItems, storageReady]);

  useEffect(() => {
    if (!storageReady) return;
    try {
      window.localStorage.setItem(
        "tellason-wishlist-v1",
        JSON.stringify(wishlistItems)
      );
    } catch {
      // Wishlist remains available for the current session.
    }
  }, [storageReady, wishlistItems]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "") => {
      const normalizedQuantity = normalizeQuantity(quantity);

      setCartItems((currentItems) => {
        const existingItem = currentItems.find(
          (item) =>
            item.id === productId && item.size === size && item.color === color
        );

        if (existingItem) {
          return currentItems.map((item) =>
            item.id === productId && item.size === size && item.color === color
              ? { ...item, quantity: item.quantity + normalizedQuantity }
              : item
          );
        }

        return [
          ...currentItems,
          { id: productId, size, color, quantity: normalizedQuantity },
        ];
      });
    },
    []
  );

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const normalizedQuantity = normalizeQuantity(quantity);

    setCartItems((currentItems) =>
      currentItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: normalizedQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    setWishlistItems((currentItems) =>
      currentItems.includes(productId)
        ? currentItems
        : [...currentItems, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlistItems((currentItems) =>
      currentItems.filter((itemId) => itemId !== productId)
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlistItems((currentItems) =>
      currentItems.includes(productId)
        ? currentItems.filter((itemId) => itemId !== productId)
        : [...currentItems, productId]
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      cartCount: cartItems.reduce((count, item) => count + item.quantity, 0),
      wishlistCount: wishlistItems.length,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
